import { DatabaseOutlined as DataManagementIcon } from '@ant-design/icons'
import { NavLink, useNavigate } from 'react-router-dom'

import { LocalStorageUtility } from '@Utils/storage/StorageUtility'

import DashboardLink from './items/dashboard-link'
import ReportsLink from './items/reports-link'
import SmartPriceLink from './items/smart-price-link'
import { logoutClick } from './large-menu'
import ReloaderNavlink from './reloader-navlink'
import HelpCenterIcon from '../../assets/images/icons/help-center-min-icon.svg?react'
import LogOutIcon from '../../assets/images/icons/log-out-min-icon.svg?react'
import Logo from '../../assets/images/icons/logo-min-icon.svg?react'
import MaximizeIcon from '../../assets/images/icons/nav-maximize-icon.svg?react'
import Rectangle from '../../assets/images/icons/rectangle.svg?react'
import {
  BillingSmallIcon,
  DashboardSmallIcon,
  DynamicPricingSmallIcon,
  ProductSmallIcon,
  ReportsSmallIcon,
  SettingsSmallIcon,
  UrlDashboardSmallIcon,
} from '../../components/icons/navigation'
import { baseApi } from '../../store/api'
import { useLogoutMutation } from '../../store/api/auth'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { unsetTokens } from '../../store/slices/authSlice'
import { IntercomEvent } from '../../utils/helpers/getIntercomMsg'
import useIntercomInternal from '../../utils/hooks/use-intercom-internal'

function SmallMenu({
  toggleMenu,
  onClickReports,
  isOnboardingCompleted,
}: {
  toggleMenu: () => void
  onClickReports: () => void
  isOnboardingCompleted?: boolean
}) {
  const [logout, { isLoading }] = useLogoutMutation()
  const { trackEventInternal } = useIntercomInternal()
  const refresh_token = useAppSelector((state) => state?.auth.refresh_token)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // activeLink color for active NavLink
  const activeLink = {
    backgroundColor: '#F5F7FF',
    color: '#2D3D85',
  }

  const handleToggleMenu = (): void => {
    if (!isOnboardingCompleted) {
      return
    }
    toggleMenu()
  }

  const handleLogout = (): void => {
    logoutClick({
      refresh_token,
      dispatch,
      trackEventInternal,
      logout,
      storage: new LocalStorageUtility(),
      navigate,
    })
  }

  return (
    <>
      <div className="relative z-[2] w-24 flex-none hidden lg:block">
        <div className="fixed text-gray-700 bottom-0 top-[--menu-top] transition-all w-24 border-r-2 border-solid border-gray-100 overflow-y-auto">
          <ul className="flex h-full flex-col items-center">
            <li className="flex flex-col pt-8 items-center">
              <button onClick={handleToggleMenu}>
                <MaximizeIcon />
              </button>
              <div className="pt-8 pb-6">
                <Rectangle />
              </div>
              <div className="pb-6">
                <Logo />
              </div>

              <DashboardLink
                to={'/'}
                className="flex flex-row items-center mb-4 rounded-md"
                style={({ isActive }) => (isActive ? activeLink : {})}
                onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
              >
                {({ isActive }) => (
                  <DashboardSmallIcon
                    stroke={isActive ? '#3E54B8' : '#667085'}
                  />
                )}
              </DashboardLink>
              <ReloaderNavlink
                to={'/products'}
                className="flex flex-row items-center mb-4 rounded-md"
                style={({ isActive }) => (isActive ? activeLink : {})}
                onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
              >
                {({ isActive }) => (
                  <ProductSmallIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                )}
              </ReloaderNavlink>
              <SmartPriceLink
                to={'/dynamic-pricing'}
                className="flex flex-row items-center p-3 mb-4 rounded-md"
                style={({ isActive }) => (isActive ? activeLink : {})}
                onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
              >
                {({ isActive }) => (
                  <DynamicPricingSmallIcon
                    stroke={isActive ? '#3E54B8' : '#667085'}
                  />
                )}
              </SmartPriceLink>
              <ReloaderNavlink
                to={'/data-management'}
                className="flex flex-row items-center mb-4 rounded-md"
                style={({ isActive }) => (isActive ? activeLink : {})}
                onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
              >
                {({ isActive }) => (
                  <DataManagementIcon
                    className={`h-[48px] w-[48px] flex justify-center [&>svg]:!h-[32px] [&>svg]:!w-[21px] ${
                      isActive
                        ? '[&>path]:stroke-[#3E54B8]'
                        : '[&>path]:stroke-[#667085]'
                    }`}
                  />
                )}
              </ReloaderNavlink>
              <ReportsLink small>
                <button
                  className="flex flex-row items-center pb-4"
                  onClick={(e) =>
                    !isOnboardingCompleted
                      ? e.preventDefault()
                      : onClickReports()
                  }
                >
                  <ReportsSmallIcon />
                </button>
              </ReportsLink>
            </li>
            <div className="flex flex-col h-full pb-6 items-center justify-end">
              <div className=" pb-6">
                <Rectangle />
              </div>
              <ul className="px-0">
                <li>
                  <NavLink
                    to={'/settings'}
                    className="flex flex-row items-center mb-4 rounded-md"
                    style={({ isActive }) => (isActive ? activeLink : {})}
                    onClick={(e) =>
                      !isOnboardingCompleted && e.preventDefault()
                    }
                  >
                    {({ isActive }) => (
                      <SettingsSmallIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                    )}
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={'404-reports/'}
                    className="flex flex-row items-center mb-4 rounded-md"
                    style={({ isActive }) => (isActive ? activeLink : {})}
                    onClick={(e) =>
                      !isOnboardingCompleted && e.preventDefault()
                    }
                  >
                    {({ isActive }) => (
                      <UrlDashboardSmallIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                    )}
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={'/billing'}
                    className="flex flex-row items-center mb-4 rounded-md"
                    style={({ isActive }) => (isActive ? activeLink : {})}
                    onClick={(e) =>
                      !isOnboardingCompleted && e.preventDefault()
                    }
                  >
                    {({ isActive }) => (
                      <BillingSmallIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                    )}
                  </NavLink>
                </li>
                <li>
                  <a
                    href="https://helpcenter.prisync.com/hc/en-us"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex flex-row items-center pb-8"
                  >
                    <HelpCenterIcon />
                  </a>
                </li>
                <li>
                  <button
                    className="flex flex-row items-center"
                    onClick={handleLogout}
                  >
                    <LogOutIcon />
                  </button>
                </li>
              </ul>
            </div>
          </ul>
        </div>
      </div>
    </>
  )
}

export default SmallMenu
